import { useEffect, useState } from 'react';
import {
  ADJUSTMENT_REASONS,
  INVENTORY_CATEGORIES,
  MOVEMENT_TYPE_LABELS,
  STOCK_UNITS,
  type MovementType,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Field, Modal, Pager, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2E — Inventory.
 *
 * Overview table with branch scope, category/search/low-stock filters,
 * an item editor, per-item movement history and the manual stock
 * in/out + adjustment actions. Every write goes through the admin-only
 * API; this page only renders what the server authorizes.
 */

export interface InventoryItemDto {
  id: string;
  branchId: string;
  branchCode?: string;
  name: string;
  category: string;
  unit: string;
  quantity: number;
  minQuantity: number;
  lowStock: boolean;
  isActive: boolean;
  notes: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

interface MovementDto {
  id: string;
  itemId: string;
  branchId: string;
  movementType: MovementType;
  quantity: number;
  unit: string;
  balanceBefore: number;
  balanceAfter: number;
  reason: string | null;
  referenceType: string | null;
  referenceId: string | null;
  referenceNumber: string | null;
  actorName: string;
  actorRole: string;
  createdAt: string;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Page size the list asks the server for (the server total drives the count). */
const PAGE_SIZE = 50;

const movementTone: Record<string, 'emerald' | 'rose' | 'amber'> = {
  STOCK_IN: 'emerald',
  STOCK_OUT: 'rose',
  ADJUSTMENT: 'amber',
};

/* ---------------- item form (create + edit) ---------------- */

interface ItemFormState {
  branchId: string;
  name: string;
  category: string;
  unit: string;
  minQuantity: string;
  notes: string;
  isActive: boolean;
}

function emptyItemForm(branchId: string): ItemFormState {
  return {
    branchId,
    name: '',
    category: INVENTORY_CATEGORIES[0],
    unit: 'bottles',
    minQuantity: '0',
    notes: '',
    isActive: true,
  };
}

function ItemFormModal({
  item,
  defaultBranchId,
  branches,
  onClose,
  onSaved,
}: {
  item: InventoryItemDto | null;
  defaultBranchId: string;
  branches: { id: string; code: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = item !== null;
  const [form, setForm] = useState<ItemFormState>(
    item
      ? {
          branchId: item.branchId,
          name: item.name,
          category: item.category,
          unit: item.unit,
          minQuantity: String(item.minQuantity),
          notes: item.notes ?? '',
          isActive: item.isActive,
        }
      : emptyItemForm(defaultBranchId),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof ItemFormState>(key: K, value: ItemFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    setError(null);
    const minQuantity = Number(form.minQuantity);
    if (!Number.isInteger(minQuantity) || minQuantity < 0) {
      setError('Minimum stock must be a whole number of 0 or more.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/inventory/${item.id}`, {
          name: form.name.trim(),
          category: form.category.trim(),
          unit: form.unit,
          minQuantity,
          notes: form.notes.trim() || null,
          isActive: form.isActive,
        });
      } else {
        await api.post('/inventory', {
          branchId: form.branchId,
          name: form.name.trim(),
          category: form.category.trim(),
          unit: form.unit,
          minQuantity,
          notes: form.notes.trim() || undefined,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save the item.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? `Edit ${item.name}` : 'New inventory item'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create item'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} />}
        {!editing && (
          <Field label="Branch" htmlFor="item-branch">
            <select
              id="item-branch"
              className={inputCls}
              value={form.branchId}
              onChange={(e) => set('branchId', e.target.value)}
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Item name" htmlFor="item-name">
          <input
            id="item-name"
            className={inputCls}
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Detergent"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Category" htmlFor="item-category" hint="Suggestions below; any category works">
            <input
              id="item-category"
              className={inputCls}
              list="inventory-categories"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
            />
            <datalist id="inventory-categories">
              {INVENTORY_CATEGORIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
          <Field label="Unit" htmlFor="item-unit">
            <select
              id="item-unit"
              className={inputCls}
              value={form.unit}
              onChange={(e) => set('unit', e.target.value)}
            >
              {STOCK_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Minimum stock (low-stock alert threshold)" htmlFor="item-min">
          <input
            id="item-min"
            type="number"
            min={0}
            className={inputCls}
            value={form.minQuantity}
            onChange={(e) => set('minQuantity', e.target.value)}
          />
        </Field>
        <Field label="Notes (optional)" htmlFor="item-notes">
          <textarea
            id="item-notes"
            className={`${inputCls} h-20 py-2`}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </Field>
        {editing && (
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600"
            />
            Active (inactive items hide from the default list)
          </label>
        )}
      </div>
    </Modal>
  );
}

/* ---------------- movement history ---------------- */

function HistoryModal({ item, onClose }: { item: InventoryItemDto; onClose: () => void }) {
  const [movements, setMovements] = useState<MovementDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Stock actions (admin-only; server enforces again)
  const [mode, setMode] = useState<'none' | 'in' | 'out' | 'adjust'>('none');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState<string>(ADJUSTMENT_REASONS[0]);
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void api
      .get<{ total: number; items: MovementDto[] }>(`/inventory/${item.id}/movements?limit=100`)
      .then((res) => {
        if (!cancelled) {
          setMovements(res.items);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id, refreshKey]);

  const runAction = async () => {
    setActionError(null);
    const value = Number(qty);
    if (!Number.isInteger(value) || value === 0) {
      setActionError(mode === 'adjust' ? 'Enter a non-zero whole number.' : 'Enter a whole number greater than zero.');
      return;
    }
    if (mode !== 'adjust' && value < 0) {
      setActionError('Quantity must be positive.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'adjust') {
        await api.post(`/inventory/${item.id}/adjustments`, {
          quantity: value,
          reason,
          note: note.trim() || undefined,
        });
      } else {
        await api.post(`/inventory/${item.id}/movements`, {
          movementType: mode === 'in' ? 'STOCK_IN' : 'STOCK_OUT',
          quantity: Math.abs(value),
          reason: note.trim() || (mode === 'in' ? 'Manual stock in' : 'Manual stock out'),
        });
      }
      setQty('');
      setNote('');
      setMode('none');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`${item.name} — movement history`}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm">
          <Badge tone={item.lowStock ? 'rose' : 'emerald'}>
            {item.quantity} {item.unit}
          </Badge>
          <span className="text-slate-500">
            min {item.minQuantity} {item.unit} · {item.branchCode} · {item.category}
          </span>
          <span className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setMode(mode === 'in' ? 'none' : 'in')}>
              Stock in
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setMode(mode === 'out' ? 'none' : 'out')}>
              Stock out
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setMode(mode === 'adjust' ? 'none' : 'adjust')}>
              Adjust
            </Button>
          </span>
        </div>

        {mode !== 'none' && (
          <Card className="space-y-3 p-3">
            <p className="text-sm font-semibold text-slate-700">
              {mode === 'in' ? 'Record stock in' : mode === 'out' ? 'Record stock out' : 'Inventory adjustment'}
            </p>
            {actionError && <ErrorBanner message={actionError} />}
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={mode === 'adjust' ? 'Quantity (signed, e.g. -2 or 3)' : 'Quantity'}
                htmlFor="mv-qty"
              >
                <input
                  id="mv-qty"
                  type="number"
                  className={inputCls}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
              </Field>
              {mode === 'adjust' ? (
                <Field label="Reason" htmlFor="mv-reason">
                  <select
                    id="mv-reason"
                    className={inputCls}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    {ADJUSTMENT_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label="Note" htmlFor="mv-note">
                  <input
                    id="mv-note"
                    className={inputCls}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Why is this movement happening?"
                  />
                </Field>
              )}
            </div>
            {mode === 'adjust' && (
              <Field label="Note (optional)" htmlFor="mv-adj-note">
                <input
                  id="mv-adj-note"
                  className={inputCls}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Extra detail for the audit trail"
                />
              </Field>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setMode('none')}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => void runAction()} disabled={busy || !qty}>
                {busy ? 'Saving…' : 'Record'}
              </Button>
            </div>
          </Card>
        )}

        {error && <ErrorBanner message={error} />}
        {loading ? (
          <div className="flex justify-center py-6">
            <Spinner className="h-6 w-6 text-indigo-600" />
          </div>
        ) : movements.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">
            No movements yet. Stock in, stock out and adjustments will appear here.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {movements.map((m) => (
              <li key={m.id} className="py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={movementTone[m.movementType] ?? 'slate'}>
                    {MOVEMENT_TYPE_LABELS[m.movementType]}
                  </Badge>
                  <span className="text-sm font-semibold text-slate-800">
                    {m.movementType === 'ADJUSTMENT' && m.quantity >= 0 ? '+' : m.movementType === 'STOCK_OUT' ? '-' : ''}
                    {m.movementType === 'ADJUSTMENT' ? Math.abs(m.quantity) : m.quantity} {m.unit}
                  </span>
                  <span className="text-xs text-slate-500">
                    {m.balanceBefore} → {m.balanceAfter}
                  </span>
                  <span className="ml-auto text-xs text-slate-400">{formatWhen(m.createdAt)}</span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">
                  {m.reason ?? '—'}
                  {m.referenceNumber ? ` · ref ${m.referenceNumber}` : ''} · by {m.actorName}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

/* ---------------- main page ---------------- */

export function InventoryPage() {
  const { branchFilter, branches } = useAuth();
  const [items, setItems] = useState<InventoryItemDto[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [categories, setCategories] = useState<string[]>([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [lowOnly, setLowOnly] = useState(false);

  const [formItem, setFormItem] = useState<InventoryItemDto | null | 'new'>(null);
  const [historyItem, setHistoryItem] = useState<InventoryItemDto | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  // A new filter means a new result set: always start from the first page.
  useEffect(() => {
    setOffset(0);
  }, [branchFilter, query, category, lowOnly]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (query) params.set('q', query);
    if (category) params.set('category', category);
    if (lowOnly) params.set('lowStock', 'true');
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));

    void api
      .get<{ total: number; items: InventoryItemDto[]; categories: string[]; lowStockCount: number }>(
        `/inventory?${params.toString()}`,
      )
      .then((res) => {
        if (!cancelled) {
          setItems(res.items);
          setTotal(res.total);
          setCategories(res.categories);
          setLowStockCount(res.lowStockCount);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load inventory.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, query, category, lowOnly, offset, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;

  return (
    <section className="space-y-4" aria-label="Inventory">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Inventory</h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · branch-specific stock with a full movement trail
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
          <Button onClick={() => setFormItem('new')}>New item</Button>
        </div>
      </div>

      {lowStockCount > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <Icon name="alert" className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">{lowStockCount} item{lowStockCount === 1 ? '' : 's'}</span> at or below
            minimum stock{lowOnly ? '' : ' — enable the low-stock filter to focus on them'}.
          </p>
        </div>
      )}

      <Card className="flex flex-wrap items-center gap-3 p-4">
        <input
          aria-label="Search inventory"
          className={`${inputCls} h-11 max-w-xs flex-1`}
          placeholder="Search item or category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Filter by category"
          className={`${inputCls} h-11 w-auto`}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={lowOnly}
            onChange={(e) => setLowOnly(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600"
          />
          Low stock only
        </label>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6 text-indigo-600" />
          </div>
        ) : total === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm font-medium text-slate-700">No inventory items match.</p>
            <p className="mt-1 text-xs text-slate-500">
              Create your first item with <span className="font-semibold">New item</span>.
            </p>
          </div>
        ) : (
          <>
            <table className="min-w-full text-sm">
              <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3 text-right">Quantity</th>
                <th className="px-4 py-3 text-right">Min</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => (
                <tr key={item.id} className={item.isActive ? '' : 'opacity-60'}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-800">{item.name}</p>
                    {item.notes && <p className="text-xs text-slate-500">{item.notes}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{item.category}</td>
                  <td className="px-4 py-3">
                    <Badge tone="indigo">{item.branchCode}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">
                    {item.quantity} <span className="font-normal text-slate-500">{item.unit}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600">{item.minQuantity}</td>
                  <td className="px-4 py-3">
                    {!item.isActive ? (
                      <Badge tone="slate">Inactive</Badge>
                    ) : item.lowStock ? (
                      <Badge tone="rose">Low stock</Badge>
                    ) : (
                      <Badge tone="emerald">In stock</Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setHistoryItem(item)}>
                      History
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setFormItem(item)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
            <Pager
              total={total}
              offset={offset}
              loaded={items.length}
              onPrev={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              onNext={() => setOffset(offset + PAGE_SIZE)}
            />
          </>
        )}
      </Card>

      {formItem !== null && (
        <ItemFormModal
          item={formItem === 'new' ? null : formItem}
          defaultBranchId={branches[0]?.id ?? ''}
          branches={branches}
          onClose={() => setFormItem(null)}
          onSaved={refresh}
        />
      )}
      {historyItem && <HistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />}
    </section>
  );
}
